import classNames from "classnames";
import React, { ForwardRefExoticComponent, RefAttributes, useMemo } from "react";
import { cloneIcon } from "../../utils/dom-utils";
import { CSS_CLASSES } from "./link-css-classes";
import { LinkProps } from "./link.types";

const externalRelTypes = ["noopener", "noreferrer"];

/**
 * The `Link` component enables users to trigger navigation actions on the user interface.
 *
 * Use the below props playground to explore the main prop it offers.
 *
 * Note that the `font-size` and `line-height` properties are inherited from the context. So you can wrap a `Link` component within a `Text` component to get link text that match standard typography styles like headings and caption-texts.
 *
 * Read on to understand the UX intent behind each of the prop values.
 */
export const Link: ForwardRefExoticComponent<LinkProps & RefAttributes<HTMLAnchorElement>> = React.forwardRef(
  (
    {
      // attributes
      className,
      tabIndex = 0,
      href,
      target,
      rel,
      onClick,
      disabled = false,
      // own props
      color,
      bold = false,
      underline = "hover",
      external = false,
      startIcon,
      endIcon,
      children,
      linkComponent,
      linkComponentProps = {},
      // rest
      ...restProps
    },
    ref
  ) => {
    const anchorClassName = useMemo(
      () =>
        classNames(
          CSS_CLASSES.LINK,
          {
            [CSS_CLASSES.LINK_BOLD]: bold,
            [CSS_CLASSES.LINK_UNDERLINE_HOVER]: underline === "hover",
            [CSS_CLASSES.LINK_UNDERLINE_ALWAYS]: underline === "always",
            [CSS_CLASSES.LINK_COLOR_PRIMARY]: color === "primary",
            [CSS_CLASSES.LINK_COLOR_SECONDARY]: color === "secondary",
            [CSS_CLASSES.LINK_COLOR_INTERACTIVE]: color === "interactive",
            [CSS_CLASSES.LINK_COLOR_ACCENT]: color === "accent",
            [CSS_CLASSES.LINK_COLOR_GRAY]: color === "gray",
            [CSS_CLASSES.LINK_COLOR_ERROR]: color === "error",
            [CSS_CLASSES.LINK_COLOR_WARNING]: color === "warning",
            [CSS_CLASSES.LINK_COLOR_SUCCESS]: color === "success",
            [CSS_CLASSES.LINK_COLOR_WHITE]: color === "white",
            [CSS_CLASSES.LINK_COLOR_BLACK]: color === "black",
          },
          className
        ),
      [bold, className, color, underline]
    );

    const startIconClassName = useMemo(() => classNames(CSS_CLASSES.LINK_ICON, CSS_CLASSES.LINK_START_ICON), []);
    const endIconClassName = useMemo(() => classNames(CSS_CLASSES.LINK_ICON, CSS_CLASSES.LINK_END_ICON), []);

    const anchorAttributes = useMemo(() => {
      const tabIndexAttribute = disabled ? -1 : tabIndex;
      const onClickAttribute = disabled ? undefined : onClick;
      const targetAttribute = target ?? (external ? "_blank" : undefined);
      let relAttribute = rel;
      if (!rel && external) {
        relAttribute = externalRelTypes.join(" ");
      } else if (rel && external) {
        relAttribute = [...rel.split(" "), ...externalRelTypes].join(" ");
      }

      return {
        className: anchorClassName,
        tabIndex: tabIndexAttribute,
        onClick: onClickAttribute,
        target: targetAttribute,
        rel: relAttribute,
        "aria-disabled": disabled,
        ref,
      };
    }, [anchorClassName, disabled, external, onClick, ref, rel, tabIndex, target]);

    if (linkComponent) {
      return React.cloneElement(linkComponent, {
        children,
        ...anchorAttributes,
        ...linkComponentProps,
      });
    }

    return (
      <a href={disabled ? undefined : href} {...anchorAttributes} {...restProps}>
        {startIcon && cloneIcon(startIcon, startIconClassName)}
        {children}
        {endIcon && cloneIcon(endIcon, endIconClassName)}
      </a>
    );
  }
);

/**
 * This is needed so that this name shows up correctly in React DevTools
 */
Link.displayName = "Link";
